import { HALF_DAY_HOURS, type AbsenceType } from '../../constants';
import type { Absence, DateIso } from '../../types/domain';

/** Demi-journées couvertes par une ou plusieurs absences, pour une date donnée. */
export interface Coverage {
  am: boolean;
  pm: boolean;
}

export interface TypedCoverage extends Coverage {
  type: AbsenceType | null;
}

// Couverture demi-journée d'une absence pour une date donnée.
// Gère l'ancien format (string = jour unique, {debut,fin} = jours pleins)
// et le nouveau ({debut, fin, debut_half:'am'|'pm', fin_half:'am'|'pm', type}).
export function absCoverage(absence: Absence, dayIso: DateIso): Coverage {
  // Ancien format : une simple chaîne = une journée pleine.
  const periode = typeof absence === 'string' ? null : absence;
  const debut = periode ? periode.debut : (absence as DateIso);
  const fin = periode ? periode.fin : (absence as DateIso);
  const startHalfDay = periode ? periode.debut_half || 'am' : 'am';
  const endHalfDay = periode ? periode.fin_half || 'pm' : 'pm';

  if (dayIso < debut || dayIso > fin) return { am: false, pm: false };

  // Une absence qui commence l'après-midi laisse le matin travaillé, et inversement pour la fin.
  const morningCovered = !(dayIso === debut && startHalfDay === 'pm');
  const afternoonCovered = !(dayIso === fin && endHalfDay === 'am');
  return { am: morningCovered, pm: afternoonCovered };
}

// Union des couvertures de toutes les absences d'une liste pour une date.
export function dayCoverage(
  absences: Absence[] | null | undefined,
  dayIso: DateIso,
): TypedCoverage {
  let morningCovered = false;
  let afternoonCovered = false;
  let firstTypeSeen: AbsenceType | null = null;

  for (const absence of absences || []) {
    const coverage = absCoverage(absence, dayIso);
    const coversSomething = coverage.am || coverage.pm;
    if (coversSomething && !firstTypeSeen) {
      firstTypeSeen = (typeof absence === 'string' ? null : absence.type) || null;
    }
    morningCovered = morningCovered || coverage.am;
    afternoonCovered = afternoonCovered || coverage.pm;
  }
  return { am: morningCovered, pm: afternoonCovered, type: firstTypeSeen };
}

export function absenceHoursOnDay(absences: Absence[] | null | undefined, iso: DateIso): number {
  const c = dayCoverage(absences, iso);
  return (c.am ? HALF_DAY_HOURS : 0) + (c.pm ? HALF_DAY_HOURS : 0);
}

export function isAbsent(absences: Absence[] | null | undefined, iso: DateIso): boolean {
  const c = dayCoverage(absences, iso);
  return c.am && c.pm; // journée entièrement absente
}
